import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import React, { useCallback, useEffect } from 'react';
import { SimpleSelect } from 'react-selectize-advizr';
import { useEffectOnce, usePrevious } from 'react-use';
import styled from 'styled-components';
import { v4 as uuidV4 } from 'uuid';
import AdvForm from '../../../CommonComponents/AdvForm';
import AdvInput from '../../../CommonComponents/AdvInput';
import AdvInputWrapper from '../../../CommonComponents/AdvInputWrapper';
import { LinkButton } from '../../../CommonComponents/Button';
import Icon from '../../../CommonComponents/Icon';
import { simpleSelectDefaultValue } from '../../../Profile/Accounts_react/AccountsUtils';
import Footer from '../../Footer/FooterContainer';
import Loading from '../../Loading/LoadingView';
import PlanImpact from '../../PlanImpact/PlanImpactContainer';
import ValidationErrors from '../../ValidationErrors/ValidationErrorsContainer';

const TimesWrapper = styled.div`
  display: flex;
  align-items: center;

  > * + * {
    margin-left: 16px !important;
  }
`;

const DeleteButton = styled(LinkButton)`
  float: right;

  ${Icon} {
    color: #dadada;
  }
`;

const AddButton = styled.button`
  display: block;
  margin: 0 auto 30px auto;
`;

enum RothConversionType {
  LumpSum,
  Recurring,
  TaxBracket,
}

const ConversionTypeOptions = [
  { label: 'Lump Sum', value: RothConversionType.LumpSum },
  { label: 'Recurring', value: RothConversionType.Recurring },
  { label: 'Tax Bracket', value: RothConversionType.TaxBracket },
];

enum TaxPaymentMethod {
  OutsideCashflow,
  ConvertedAccount,
}

const TaxPaymentMethodOptions = [
  { label: 'Use outside cashflow', value: TaxPaymentMethod.OutsideCashflow },
  { label: 'Pay with converted account', value: TaxPaymentMethod.ConvertedAccount },
];

export const RothConversionWhatIf: React.FC<any> = ({
  clientId,
  fpId,
  goalId,
  areOptionsLoaded,
  whatIf,
  whatIfTemplate,
  loading,
  groupTitle,
  planImpactItems,
  fetchData,
  fetchInitialPlanImpact,
  fetchPlanImpact,
  storeData,
  options,
  conversions,
  applyWhatIf,
  isOPLMonteCarlo,
  showTaxBracketOption,
}) => {
  useEffectOnce(() => {
    fetchData(clientId, fpId, goalId, whatIfTemplate.WhatIfType);
    fetchInitialPlanImpact(clientId, fpId, goalId, whatIf, whatIfTemplate);
  });

  const previousConversions = usePrevious(conversions);
  const previousIsOPLMonteCarlo = usePrevious(isOPLMonteCarlo);

  useEffect(() => {
    if (
      conversions.every(conversion => {
        if (['From', 'To', 'TaxPaymentMethod'].some(k => isNil(conversion[k]))) {
          return false;
        }

        switch (conversion.Type) {
          case RothConversionType.LumpSum:
            return !isNil(conversion.Amount);
          case RothConversionType.Recurring:
            return !isNil(conversion.Duration) && !isNil(conversion.Amount);
          case RothConversionType.TaxBracket:
            return !isNil(conversion.Duration) && !isNil(conversion.TaxBracketPercentage);
          default:
            return false;
        }
      }) &&
      ((previousConversions && !isEqual(previousConversions, conversions)) ||
        !isEqual(previousIsOPLMonteCarlo, isOPLMonteCarlo))
    ) {
      fetchPlanImpact(clientId, fpId, goalId, whatIf, whatIfTemplate);
    }
  }, [
    fetchPlanImpact,
    clientId,
    fpId,
    goalId,
    whatIf,
    whatIfTemplate,
    conversions,
    previousConversions,
    previousIsOPLMonteCarlo,
    isOPLMonteCarlo,
  ]);

  const addConversion = useCallback(() => {
    storeData(clientId, goalId, whatIfTemplate.WhatIfType, {
      WhatIf: {
        Conversions: [
          ...conversions,
          {
            Id: uuidV4(),
            Type: RothConversionType.LumpSum,
            Amount: 0,
            TaxPaymentMethod: TaxPaymentMethod.OutsideCashflow,
          },
        ],
      },
    });
  }, [storeData, clientId, goalId, whatIfTemplate, conversions]);

  const updateConversion = useCallback(
    (i, conversion) => {
      const updatedConversion = conversion;

      if (conversion.EntireBalance && conversion.From) {
        const fromAccount = options.FromAccounts.find(({ value }) => value === conversion.From);

        if (fromAccount) {
          updatedConversion.Amount = fromAccount.balance;
        }
      }

      storeData(clientId, goalId, whatIfTemplate.WhatIfType, {
        WhatIf: {
          Conversions: [...conversions.slice(0, i), updatedConversion, ...conversions.slice(i + 1)],
        },
      });
    },
    [storeData, clientId, goalId, whatIfTemplate, conversions, options],
  );

  const deleteConversion = useCallback(
    i => {
      storeData(clientId, goalId, whatIfTemplate.WhatIfType, {
        WhatIf: {
          Conversions: [...conversions.slice(0, i), ...conversions.slice(i + 1)],
        },
      });
    },
    [storeData, clientId, goalId, whatIfTemplate, conversions],
  );

  const filteredConversionOptions = ConversionTypeOptions.filter(
    option => showTaxBracketOption || option.value !== RothConversionType.TaxBracket,
  );

  return (
    <div className="what-ifs-layout">
      {(loading || !areOptionsLoaded) && <Loading />}
      <div className="what-ifs-section what-ifs-layout-header">
        <h3>What-If...</h3>
      </div>
      <div className="what-ifs-layout-content">
        <h4 className="what-if-group-title">{groupTitle}</h4>
        <AdvForm>
          {/* @ts-ignore */}
          <ValidationErrors clientId={clientId} />

          {areOptionsLoaded &&
            conversions.map((conversion, i) => {
              const fromAccount = options.FromAccounts.find(
                ({ value }) => value === conversion.From,
              );

              const toAccountsOptions = options.ToAccounts.filter(
                ({ owner }) => owner === fromAccount?.owner,
              );

              return (
                // eslint-disable-next-line react/no-array-index-key
                <ul key={i} className="what-ifs-form-fields what-ifs-box loaded">
                  <DeleteButton onClick={() => deleteConversion(i)}>
                    <Icon className="fas fa-times-circle" />
                  </DeleteButton>
                  <li className="what-ifs-form-field what-ifs-form-field-no-box inline">
                    <label htmlFor={`ConversionType-${i}`}>
                      <span className="what-if-title">Type</span>
                      <SimpleSelect
                        id={`ConversionType-${i}`}
                        className="what-ifs-simple-select"
                        value={simpleSelectDefaultValue(filteredConversionOptions, conversion.Type)}
                        onValueChange={({ value }) =>
                          updateConversion(i, { ...conversion, Type: value })
                        }
                        options={filteredConversionOptions}
                      />
                    </label>
                  </li>
                  <li className="what-ifs-form-field what-ifs-form-field-no-box inline">
                    <label htmlFor={`AccountFrom-${i}`}>
                      <span className="what-if-title">From</span>
                      <SimpleSelect
                        id={`AccountFrom-${i}`}
                        className="what-ifs-simple-select"
                        value={simpleSelectDefaultValue(options.FromAccounts, conversion.From)}
                        onValueChange={({ value }) =>
                          updateConversion(i, { ...conversion, From: value, To: null })
                        }
                        options={options.FromAccounts}
                      />
                    </label>
                  </li>
                  <li className="what-ifs-form-field what-ifs-form-field-no-box inline">
                    <label htmlFor={`AccountTo-${i}`}>
                      <span className="what-if-title">To</span>
                      <SimpleSelect
                        id={`AccountTo-${i}`}
                        className="what-ifs-simple-select"
                        disabled={!conversion.From}
                        value={simpleSelectDefaultValue(toAccountsOptions, conversion.To)}
                        onValueChange={({ value }) =>
                          updateConversion(i, { ...conversion, To: value })
                        }
                        options={toAccountsOptions}
                      />
                    </label>
                  </li>
                  <li className="what-ifs-form-field what-ifs-form-field-no-box inline">
                    <label htmlFor={`Year-${i}`}>
                      <span className="what-if-title">Year</span>
                      <SimpleSelect
                        id={`Year-${i}`}
                        className="what-ifs-simple-select"
                        value={simpleSelectDefaultValue(options.Years, conversion.Year)}
                        onValueChange={({ value }) =>
                          updateConversion(i, { ...conversion, Year: value })
                        }
                        options={options.Years}
                      />
                    </label>
                  </li>
                  {(conversion.Type === RothConversionType.LumpSum ||
                    conversion.Type === RothConversionType.Recurring) && (
                    <li className="what-ifs-form-field what-ifs-form-field-no-box inline">
                      <label htmlFor="Amount">
                        <span className="what-if-title">Amount</span>

                        <AdvInputWrapper hasCurrency className="what-ifs-input-wrapper">
                          <AdvInput
                            disabled={
                              conversion.Type === RothConversionType.LumpSum &&
                              conversion.EntireBalance
                            }
                            type="text"
                            dataType="money"
                            maxLength="14"
                            onBlur={value => updateConversion(i, { ...conversion, Amount: value })}
                            value={conversion.Amount}
                            className="what-ifs-input"
                          />
                        </AdvInputWrapper>
                      </label>
                    </li>
                  )}
                  {conversion.Type === RothConversionType.LumpSum && (
                    <li className="what-ifs-form-field what-ifs-form-field-no-box inline">
                      <div>
                        <span className="what-if-title" />
                        <label
                          htmlFor={`EntireBalance-${i}`}
                          className="what-ifs-checkbox no-padding"
                          style={{ width: 'auto', position: 'relative' }}
                        >
                          <input
                            type="checkbox"
                            id={`EntireBalance-${i}`}
                            className="what-ifs-checkbox-toggle"
                            checked={conversion.EntireBalance}
                            onChange={ev =>
                              updateConversion(i, {
                                ...conversion,
                                EntireBalance: ev.target.checked,
                              })
                            }
                          />
                          <i className="what-ifs-checkbox-mark" />
                          <span className="what-ifs-checkbox-label-text">Entire Account</span>
                        </label>
                      </div>
                    </li>
                  )}
                  {(conversion.Type === RothConversionType.Recurring ||
                    conversion.Type === RothConversionType.TaxBracket) && (
                    <li className="what-ifs-form-field what-ifs-form-field-no-box">
                      <label htmlFor="Times">
                        <span className="what-if-title">How many conversions</span>
                        <TimesWrapper>
                          <AdvInputWrapper className="what-ifs-input-wrapper small">
                            <AdvInput
                              nullable
                              disabled={conversion.UntilEndOfPlan}
                              type="text"
                              maxLength="2"
                              onBlur={value =>
                                updateConversion(i, { ...conversion, Duration: value })
                              }
                              value={conversion.Duration === 0 ? '' : conversion.Duration}
                              className="what-ifs-input"
                              blankable
                            />
                          </AdvInputWrapper>

                          <label
                            htmlFor={`UntilEndOfPlan-${i}`}
                            className="what-ifs-checkbox no-padding"
                            style={{ position: 'relative' }}
                          >
                            <input
                              type="checkbox"
                              id={`UntilEndOfPlan-${i}`}
                              className="what-ifs-checkbox-toggle"
                              checked={conversion.UntilEndOfPlan}
                              onChange={ev =>
                                updateConversion(i, {
                                  ...conversion,
                                  Duration: ev.target.checked ? 0 : conversion.Duration,
                                  UntilEndOfPlan: ev.target.checked,
                                })
                              }
                            />
                            <i className="what-ifs-checkbox-mark" />
                            <span className="what-ifs-checkbox-label-text">Until End of Plan</span>
                          </label>
                        </TimesWrapper>
                      </label>
                    </li>
                  )}
                  {conversion.Type === RothConversionType.TaxBracket && (
                    <li className="what-ifs-form-field what-ifs-form-field-no-box inline sentence">
                      <label htmlFor={`TaxBracketPercentage-${i}`}>
                        <span className="what-if-title">Fill</span>
                        <SimpleSelect
                          id={`TaxBracketPercentage-${i}`}
                          className="what-ifs-simple-select"
                          value={simpleSelectDefaultValue(
                            options.TaxBrackets,
                            conversion.TaxBracketPercentage,
                          )}
                          onValueChange={({ value }) =>
                            updateConversion(i, {
                              ...conversion,
                              TaxBracketPercentage: value,
                            })
                          }
                          options={options.TaxBrackets}
                        />
                        <span className="what-if-title">Tax Bracket</span>
                      </label>
                    </li>
                  )}
                  <li className="what-ifs-form-field what-ifs-form-field-no-box">
                    <label htmlFor={`TaxPaymentMethod-${i}`}>
                      <span className="what-if-title">How will you pay the taxes?</span>
                      <SimpleSelect
                        id={`TaxPaymentMethod-${i}`}
                        className="what-ifs-simple-select"
                        value={simpleSelectDefaultValue(
                          TaxPaymentMethodOptions,
                          conversion.TaxPaymentMethod,
                        )}
                        onValueChange={({ value }) =>
                          updateConversion(i, { ...conversion, TaxPaymentMethod: value })
                        }
                        options={TaxPaymentMethodOptions}
                      />
                    </label>
                  </li>
                </ul>
              );
            })}

          <AddButton
            type="button"
            className="what-ifs-aa-detailed-breakdown"
            onClick={addConversion}
          >
            Add new conversion
          </AddButton>
        </AdvForm>
      </div>

      {/* @ts-ignore */}
      <PlanImpact clientId={clientId} planImpactItems={planImpactItems} />

      {/* @ts-ignore */}
      <Footer
        clientId={clientId}
        applyToPlanCallback={applyWhatIf(clientId, fpId, goalId, whatIf, whatIfTemplate)}
      />
    </div>
  );
};
